// What a subagent is FOR, read from what the API already sends (no new
// field): the subagent's header status line, its primary action and which
// lists it belongs in all follow from this one word.
//
//   on_call   an agent may call it: `spawnable_by` names agents (or "*"),
//             or a lead subagent may spawn it (spawnersOf counts both).
//   job_team  one of the job team's own members (backend
//             services/jobs/team.yaml) and nothing calls it: jobs use it.
//   pipeline  nothing calls it: workflows start it themselves (a template's
//             or a workflow's own subagent).
//
// A job-team member someone opened to agents reads as on_call: the choice
// the operator made wins over where it came from. The "Who can call it?"
// radios (blueprints/WhoCanCall.tsx) map onto the same `spawnable_by`:
// callChoice / callValue, pure so the selftest covers both directions.

import type { SubagentBlueprintView } from "@/api/subagentBlueprints";

import { spawnersOf } from "./blueprints/blueprintModel";

/** The job team's templates (backend/lattice_api/services/jobs/team.yaml). */
export const JOB_TEAM_TEMPLATES: ReadonlySet<string> = new Set([
  "job-lead",
  "writing-lead",
  "reviewer",
  "data-checker",
  "code-reviewer",
  "general-worker",
]);

export type SubagentPurpose = "on_call" | "job_team" | "pipeline";

/** on_call: an agent may call it (spawnable_by non-empty, or a lead spawns it); job_team: in JOB_TEAM_TEMPLATES and nothing calls it; pipeline: nothing calls it (workflows only). */
export function subagentPurpose(view: SubagentBlueprintView): SubagentPurpose {
  const who = spawnersOf(view);
  if (who === "all" || who.length > 0) return "on_call";
  return JOB_TEAM_TEMPLATES.has(view.template) ? "job_team" : "pipeline";
}

// ─── "Who can call it?" (WhoCanCall) ↔ subagent.spawnable_by ───

/** any = ["*"]; only = named agents; none = [] (only workflows and jobs). */
export type CallChoice = "any" | "only" | "none";

/**
 * The radio a `spawnable_by` value shows. `[]` reads as "none" unless the
 * operator has just picked "Only…" and not yet ticked an agent (`choosing`).
 */
export function callChoice(spawnableBy: readonly string[], choosing = false): CallChoice {
  if (spawnableBy.includes("*")) return "any";
  if (spawnableBy.length > 0) return "only";
  return choosing ? "only" : "none";
}

/** The value a radio writes; "only" brings back the agents last chosen, if any. */
export function callValue(choice: CallChoice, lastNamed: readonly string[] = []): string[] {
  if (choice === "any") return ["*"];
  if (choice === "none") return [];
  return lastNamed.filter((t) => t !== "*");
}
